import { Download, FileText, X } from '@/components/ui/icons';
import { useState } from 'react';
import type { AttachmentDTO } from '@clanfire/shared';
import { Modal } from '@/components/ui/Modal';
import { cn, formatBytes } from '@/lib/utils';
import { VoiceMessage } from './VoiceMessage';

const MAX_W = 400;
const MAX_H = 320;

function fit(width: number | null, height: number | null) {
  if (!width || !height) return { width: undefined, height: undefined };
  const scale = Math.min(1, MAX_W / width, MAX_H / height);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** Over a spoiler until it's tapped. Fixed colours: it sits on a blurred picture, in either theme. */
function SpoilerTag() {
  return (
    <span className="absolute inset-0 grid place-items-center">
      <span className="rounded-full bg-black/70 px-3 py-1 text-xs font-extrabold tracking-wide text-white">SPOILER</span>
    </span>
  );
}

export function Attachments({ attachments }: { attachments: AttachmentDTO[] }) {
  const [lightbox, setLightbox] = useState<AttachmentDTO | null>(null);
  const [shown, setShown] = useState<ReadonlySet<string>>(new Set());
  if (attachments.length === 0) return null;
  const veiled = (a: AttachmentDTO) => !!a.spoiler && !shown.has(a.id);
  const reveal = (id: string) => setShown((s) => new Set(s).add(id));

  return (
    <div className="mt-1.5 flex flex-col gap-2">
      {attachments.map((a) => {
        if (a.durationMs != null && a.url) return <VoiceMessage key={a.id} attachment={a} />;
        if (a.contentType.startsWith('image/') && a.url) {
          const size = fit(a.width, a.height);
          const hidden = veiled(a);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => (hidden ? reveal(a.id) : setLightbox(a))}
              aria-label={hidden ? 'Spoiler: show image' : `Open image: ${a.description || a.fileName}`}
              className="relative block w-fit max-w-full overflow-hidden rounded-xl border border-line bg-sunken"
            >
              <img
                src={a.url}
                alt={hidden ? '' : a.description || a.fileName}
                title={hidden ? undefined : (a.description ?? undefined)}
                loading="lazy"
                width={size.width}
                height={size.height}
                style={{ maxWidth: `min(100%, ${MAX_W}px)`, maxHeight: MAX_H }}
                className={cn('block h-auto object-contain', hidden && 'scale-110 blur-2xl')}
              />
              {hidden && <SpoilerTag />}
            </button>
          );
        }
        if (a.contentType.startsWith('video/') && a.url) {
          const hidden = veiled(a);
          return (
            <div key={a.id} className="relative w-fit max-w-full overflow-hidden rounded-xl">
              <video
                src={a.url}
                controls={!hidden}
                preload="metadata"
                aria-label={a.description || a.fileName}
                className={cn('max-h-80 max-w-full rounded-xl border border-line bg-black', hidden && 'blur-2xl')}
              />
              {hidden && (
                <button type="button" onClick={() => reveal(a.id)} aria-label="Spoiler: show video" className="absolute inset-0">
                  <SpoilerTag />
                </button>
              )}
            </div>
          );
        }
        if (a.contentType.startsWith('audio/') && a.url) {
          return (
            <div key={a.id} className="w-full max-w-md rounded-xl border border-line bg-sunken p-3">
              <p className="mb-2 truncate text-sm font-semibold">{a.fileName}</p>
              <audio src={a.url} controls preload="none" className="w-full" />
            </div>
          );
        }
        return (
          <div key={a.id} className="flex w-full max-w-md items-center gap-3 rounded-xl border border-line bg-sunken p-3">
            <FileText size={32} className="shrink-0 text-accent" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-link">{a.fileName}</p>
              <p className="text-xs text-fg-muted">{formatBytes(a.size)}</p>
            </div>
            {a.url && (
              <a href={a.url} download={a.fileName} aria-label={`Download ${a.fileName}`} className="rounded-lg p-2 text-fg-muted hover:bg-hover hover:text-fg">
                <Download size={18} />
              </a>
            )}
          </div>
        );
      })}

      <Modal open={!!lightbox} onClose={() => setLightbox(null)} size="lg" bare className="bg-transparent! border-none! shadow-none!">
        {lightbox && (
          <div className="relative flex flex-col items-center gap-3">
            <img src={lightbox.url} alt={lightbox.description || lightbox.fileName} className="max-h-[80dvh] max-w-full rounded-xl object-contain" />
            {lightbox.description && <p className="max-w-prose text-center text-sm text-white">{lightbox.description}</p>}
            <div className="flex items-center gap-3 text-sm text-white">
              <a href={lightbox.url} target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">
                Open original
              </a>
              <button type="button" onClick={() => setLightbox(null)} className="inline-flex items-center gap-1 rounded-lg bg-white/15 px-2 py-1 hover:bg-white/25">
                <X size={14} /> Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
